import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Modal from '@common/Modal'
import { declOfNum } from '@helpers/declOfNum'
import { RatingIcon } from '@/utils/icons'
import { useSelector } from 'react-redux'
import { isInterfaceAnimationsDisabled } from '@/helpers/interfaceMotionSettings'
import s from './DailyLoginRewardModal.module.css'
import { usersAPI } from '@/api'
import { selectProfile } from '@store/profile.reducer'

const STORAGE_KEY = 'dailyLoginReward'
const ACHIEVEMENT_STORAGE_KEY = 'achievementReward'
const ACHIEVEMENT_POLL_INTERVAL = 60000
const ACHIEVEMENT_MIN_POLL_GAP = 15000

const DailyLoginRewardModal = () => {
  const settings = useSelector((store) => store.settings)
  const profile = useSelector(selectProfile)
  const [reward, setReward] = useState(null)
  const [rewardType, setRewardType] = useState(null)
  const currentDayRef = useRef(null)
  const lastPollRef = useRef(0)
  const inFlightRef = useRef(false)

  const loadReward = useCallback(() => {
    const achievementRaw = localStorage.getItem(ACHIEVEMENT_STORAGE_KEY)
    const raw = achievementRaw || localStorage.getItem(STORAGE_KEY)
    if (!raw) return

    try {
      setReward(JSON.parse(raw))
      setRewardType(achievementRaw ? 'achievement' : 'daily')
    } catch (error) {
      localStorage.removeItem(achievementRaw ? ACHIEVEMENT_STORAGE_KEY : STORAGE_KEY)
    }
  }, [])

  useEffect(() => {
    loadReward()
    window.addEventListener('gamificationRewardUpdated', loadReward)

    return () => {
      window.removeEventListener('gamificationRewardUpdated', loadReward)
    }
  }, [loadReward])

  const pollAchievementReward = useCallback(async () => {
    if (
      !profile?.id ||
      profile?.role !== 'children' ||
      localStorage.getItem(ACHIEVEMENT_STORAGE_KEY)
    ) {
      return
    }

    // На скрытой вкладке модалку всё равно никто не увидит, а запрос пересчитывает
    // все достижения на бэке — не ходим в API, пока вкладка не станет видимой.
    if (document.visibilityState === 'hidden') {
      return
    }

    // Отсекаем дубли, когда focus и visibilitychange приходят подряд.
    const now = Date.now()
    if (inFlightRef.current || now - lastPollRef.current < ACHIEVEMENT_MIN_POLL_GAP) {
      return
    }
    lastPollRef.current = now
    inFlightRef.current = true

    try {
      const response = await usersAPI.getUser({
        id: profile.id,
        expand: ['achievement_reward'],
      })
      const achievementReward = response?.data?.achievement_reward

      if (achievementReward) {
        localStorage.setItem(
          ACHIEVEMENT_STORAGE_KEY,
          JSON.stringify(achievementReward),
        )
        window.dispatchEvent(new Event('gamificationRewardUpdated'))
      }
    } catch (error) {
      console.error(error)
    } finally {
      inFlightRef.current = false
    }
  }, [profile?.id, profile?.role])

  useEffect(() => {
    if (profile?.role !== 'children') return

    const poll = () => pollAchievementReward()

    poll()
    const interval = setInterval(poll, ACHIEVEMENT_POLL_INTERVAL)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        poll()
      }
    }
    window.addEventListener('focus', poll)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(interval)
      window.removeEventListener('focus', poll)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [pollAchievementReward, profile?.role])

  const days = useMemo(() => {
    return Array.isArray(reward?.days) ? reward.days : []
  }, [reward])

  useEffect(() => {
    if (!reward) return

    currentDayRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
    })
  }, [reward])

  const close = () => {
    localStorage.removeItem(
      rewardType === 'achievement' ? ACHIEVEMENT_STORAGE_KEY : STORAGE_KEY,
    )
    setReward(null)
    setRewardType(null)
    setTimeout(loadReward, 0)
  }

  if (!reward) return null

  const isAchievement = rewardType === 'achievement'
  const isMotionDisabled = isInterfaceAnimationsDisabled(settings)
  const currentDayIndex = days.findIndex((day) => day.day === reward.day)
  const progress = days.length
    ? ((Math.max(currentDayIndex, 0) + 1) / days.length) * 100
    : 0
  const dayTitle = declOfNum(reward.day, ['день', 'дня', 'дней'])
  const amountTitle = declOfNum(reward.amount, ['балл', 'балла', 'баллов'])

  return (
    <Modal
      active={Boolean(reward)}
      setActive={close}
      className={s.modal}
    >
      <div className={s.card}>
        <button type="button" className={s.close} onClick={close}>
          ×
        </button>

        {!isMotionDisabled && (
          <div className={s.confetti} aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
        )}

        <div className={s.badge} aria-hidden="true">
          <span className={s.badgeIcon}>
            <svg viewBox="0 0 34 34" className={s.checkIcon}>
              <path d="M9.5 17.4L14.6 22.5L24.8 11.6" />
            </svg>
          </span>
        </div>

        <div className={s.title}>Поздравляем!</div>
        {isAchievement ? (
          <div className={s.description}>
            Вы получили достижение{' '}
            <span>{reward.title || 'Новый уровень'}</span> и заработали
          </div>
        ) : (
          <div className={s.description}>
            Вы заходили на платформу{' '}
            <span>
              {reward.day} {dayTitle} подряд
            </span>{' '}
            и заработали
          </div>
        )}
        <div className={s.amount}>
          {reward.amount} {amountTitle}!
        </div>

        {!isAchievement && (
          <>
            <div
              className={[
                s.days,
                days.length > 5 ? s.compactDays : '',
              ].join(' ')}
            >
              {days.map((day) => {
                const isPassed = day.day < reward.day
                const isCurrent = day.day === reward.day

                return (
                  <div
                    key={day.day}
                    ref={isCurrent ? currentDayRef : null}
                    className={[
                      s.day,
                      isPassed ? s.passed : '',
                      isCurrent ? s.current : '',
                    ].join(' ')}
                  >
                    <span className={s.dayAmount}>
                      {day.amount}
                      <RatingIcon className={s.fireIcon} />
                    </span>
                    <small>День {day.day}</small>
                  </div>
                )
              })}
            </div>

            <div className={s.progress}>
              <div style={{ width: `${progress}%` }} />
            </div>
          </>
        )}

        <button type="button" className={s.okButton} onClick={close}>
          Отлично!
        </button>
      </div>
    </Modal>
  )
}

export default DailyLoginRewardModal
